/**
 * budgetService.js
 * Service for Firestore budget documents and accounting transactions.
 * Supports multi-year budgets, custom account management, and real-time synchronization.
 */

import { firestore as db, auth } from "@/plugins/firebase.app";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  writeBatch,
} from "firebase/firestore";

const BUDGETS_COLLECTION = "budgets";
const TRANSACTIONS_COLLECTION = "budget_transactions";

/**
 * Returns current authenticated user metadata for change logs
 */
export function getCurrentUserInfo() {
  const u = auth?.currentUser;
  if (!u) {
    return {
      uid: "system",
      name: "Järjestelmä / Tuntematon",
      email: "",
    };
  }
  return {
    uid: u.uid || "",
    name: u.displayName || u.email || "Käyttäjä",
    email: u.email || "",
  };
}

/**
 * Strips undefined properties recursively so Firestore setDoc/updateDoc never rejects payloads
 */
function cleanFirestoreData(data) {
  return JSON.parse(JSON.stringify(data));
}

/**
 * Lists all existing budget documents (years)
 */
export async function getAvailableBudgetYears() {
  try {
    const snap = await getDocs(collection(db, BUDGETS_COLLECTION));
    const years = [];
    snap.forEach((d) => {
      const data = d.data();
      if (data.year) {
        years.push(parseInt(data.year, 10));
      } else {
        const parsed = parseInt(d.id, 10);
        if (!isNaN(parsed)) years.push(parsed);
      }
    });
    // Ensure unique & sorted descending
    const unique = [...new Set(years)].sort((a, b) => b - a);
    if (!unique.includes(2026)) {
      unique.unshift(2026);
    }
    return unique;
  } catch (error) {
    console.error("Error fetching budget years:", error);
    return [2026];
  }
}

/**
 * Subscribes to a specific year's budget document in real-time
 */
export function subscribeBudget(year, callback) {
  const docRef = doc(db, BUDGETS_COLLECTION, String(year));
  return onSnapshot(
    docRef,
    (snap) => {
      if (snap.exists()) {
        callback({ id: snap.id, ...snap.data() });
      } else {
        callback(null);
      }
    },
    (err) => {
      console.error(`Error in subscribeBudget(${year}):`, err);
    },
  );
}

/**
 * Records a history snapshot and changelog event into budgets/{year}/history
 */
export async function recordBudgetHistory(year, meta = {}) {
  try {
    const yearNum = parseInt(year, 10);
    const budgetDocRef = doc(db, BUDGETS_COLLECTION, String(yearNum));
    const budgetSnap = await getDoc(budgetDocRef);
    if (!budgetSnap.exists()) return null;

    const budgetData = budgetSnap.data();

    // Fetch transactions for this year
    const q = query(
      collection(db, TRANSACTIONS_COLLECTION),
      where("year", "==", yearNum),
    );
    const txSnap = await getDocs(q);
    const transactions = [];
    txSnap.forEach((d) => transactions.push({ id: d.id, ...d.data() }));

    // Calculate metrics
    let totalAccounts = 0;
    let incomeArvio = 0;
    let expenseArvio = 0;
    let totalToteutuma = 0;

    (budgetData.projects || []).forEach((p) => {
      (p.accounts || []).forEach((a) => {
        if (!a.isTotal && !a.isTitle) {
          totalAccounts++;
          const val = Number(a.arvio) || 0;
          if (a.type === "income") incomeArvio += val;
          else if (a.type === "expense") expenseArvio += val;
        }
      });
    });

    transactions.forEach((tx) => {
      if (tx.status === "assigned") {
        totalToteutuma += Number(tx.totalAmount) || 0;
      }
    });

    const now = new Date();
    const historyId = `hist_${now.getTime()}_${Math.random()
      .toString(36)
      .substring(2, 7)}`;
    const historyRef = doc(
      db,
      BUDGETS_COLLECTION,
      String(yearNum),
      "history",
      historyId,
    );

    const historyPayload = cleanFirestoreData({
      id: historyId,
      year: yearNum,
      timestamp: now.toISOString(),
      user: getCurrentUserInfo(),
      actionType: meta.actionType || "UPDATE",
      summary: meta.summary || "Budjettia muokattu",
      details: meta.details || "",
      metrics: {
        projectCount: (budgetData.projects || []).length,
        accountCount: totalAccounts,
        transactionCount: transactions.length,
        incomeArvio: Math.round(incomeArvio * 100) / 100,
        expenseArvio: Math.round(expenseArvio * 100) / 100,
        netArvio: Math.round((incomeArvio - expenseArvio) * 100) / 100,
        totalToteutuma: Math.round(totalToteutuma * 100) / 100,
      },
      budgetSnapshot: budgetData,
      transactionsSnapshot: transactions,
    });

    await setDoc(historyRef, historyPayload);
    return historyId;
  } catch (err) {
    console.error(`Error recording budget history for year ${year}:`, err);
    return null;
  }
}

/**
 * Subscribes to the change log / history timeline of a budget year
 */
export function subscribeBudgetHistory(year, callback) {
  const historyColRef = collection(
    db,
    BUDGETS_COLLECTION,
    String(year),
    "history",
  );
  return onSnapshot(
    historyColRef,
    (snap) => {
      const list = [];
      snap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      // Sort descending by timestamp
      list.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      callback(list);
    },
    (err) => {
      console.error(`Error in subscribeBudgetHistory(${year}):`, err);
    },
  );
}

/**
 * Restores a specific budget version from history (point-in-time rollback)
 */
export async function restoreBudgetVersion(year, historyId) {
  const yearNum = parseInt(year, 10);
  const histDocRef = doc(
    db,
    BUDGETS_COLLECTION,
    String(yearNum),
    "history",
    historyId,
  );
  const histSnap = await getDoc(histDocRef);
  if (!histSnap.exists()) {
    throw new Error("Historiatietuetta ei löytynyt.");
  }

  const histData = histSnap.data();
  if (!histData.budgetSnapshot) {
    throw new Error("Historiatietue ei sisällä budjettivedosta.");
  }

  // 1. Take safety backup of current state before rollback
  await recordBudgetHistory(yearNum, {
    actionType: "AUTO_BACKUP",
    summary: "Automaattinen varmuuskopio ennen versiopalautusta",
    details: `Palautetaan versioon: ${histData.summary} (${new Date(
      histData.timestamp,
    ).toLocaleString("fi-FI")})`,
  });

  // 2. Overwrite budget document with snapshot
  const budgetDocRef = doc(db, BUDGETS_COLLECTION, String(yearNum));
  await setDoc(
    budgetDocRef,
    cleanFirestoreData({
      ...histData.budgetSnapshot,
      year: yearNum,
      updatedAt: new Date().toISOString(),
    }),
  );

  // 3. Restore transactions for this year
  const targetTxList = histData.transactionsSnapshot || [];
  const targetTxIds = new Set(targetTxList.map((t) => t.id));

  // Find existing transactions for this year to clean up ones not in snapshot
  const q = query(
    collection(db, TRANSACTIONS_COLLECTION),
    where("year", "==", yearNum),
  );
  const currentTxSnap = await getDocs(q);
  const toDelete = [];
  currentTxSnap.forEach((d) => {
    if (!targetTxIds.has(d.id)) {
      toDelete.push(d.id);
    }
  });

  // Delete orphans
  const chunkSize = 400;
  for (let i = 0; i < toDelete.length; i += chunkSize) {
    const batch = writeBatch(db);
    toDelete.slice(i, i + chunkSize).forEach((id) => {
      batch.delete(doc(db, TRANSACTIONS_COLLECTION, id));
    });
    await batch.commit();
  }

  // Save/overwrite target transactions
  for (let i = 0; i < targetTxList.length; i += chunkSize) {
    const batch = writeBatch(db);
    targetTxList.slice(i, i + chunkSize).forEach((tx) => {
      const docId = tx.id || `${tx.year}_${tx.tositelaji}_${tx.tositenro}`;
      batch.set(
        doc(db, TRANSACTIONS_COLLECTION, docId),
        cleanFirestoreData({
          ...tx,
          id: docId,
          updatedAt: new Date().toISOString(),
        }),
        { merge: true },
      );
    });
    await batch.commit();
  }

  // 4. Record the restore action in history
  await recordBudgetHistory(yearNum, {
    actionType: "RESTORE",
    summary: `Palautettu aiempi versio (${new Date(
      histData.timestamp,
    ).toLocaleString("fi-FI")})`,
    details: `Palautettu kohteesta: "${histData.summary}" (Tekijä: ${
      histData.user?.name || "Tuntematon"
    })`,
  });
}

/**
 * Saves or overwrites a budget document for a given year
 */
export async function saveBudget(year, budgetData, historyMeta = null) {
  const docRef = doc(db, BUDGETS_COLLECTION, String(year));
  const payload = cleanFirestoreData({
    ...budgetData,
    year: parseInt(year, 10),
    updatedAt: new Date().toISOString(),
  });
  await setDoc(docRef, payload, { merge: true });

  await recordBudgetHistory(
    year,
    historyMeta || {
      actionType: "CREATE",
      summary: `Budjetti tallennettu vuodelle ${year}`,
    },
  );
}

/**
 * Adds a new account to a specific project within the year's budget
 */
export async function addAccountToProject(
  year,
  projectId,
  account,
  historyMeta = null,
) {
  const docRef = doc(db, BUDGETS_COLLECTION, String(year));
  const snap = await getDoc(docRef);
  if (!snap.exists()) {
    throw new Error(`Budjettia vuodelle ${year} ei löytynyt.`);
  }

  const budget = snap.data();
  const projects = budget.projects || [];
  const project = projects.find(
    (p) =>
      String(p.id) === String(projectId) ||
      String(p.code) === String(projectId),
  );
  if (!project) {
    throw new Error(`Kustannuspaikkaa ${projectId} ei löytynyt.`);
  }

  if (!project.accounts) project.accounts = [];

  const isTitle = Boolean(account.isTitle || account.isHeader);
  const accountId =
    account.id ||
    `acc_${projectId}_${isTitle ? "title_" : "custom_"}${
      account.accountNumber || "acc"
    }_${Date.now()}`;

  const newAccount = {
    ...account,
    id: accountId,
    isTitle,
    isHeader: isTitle,
    isModifiable: !isTitle,
    arvio: parseFloat(account.arvio) || 0,
    baseToteutuma: parseFloat(account.baseToteutuma) || 0,
    isCustom: true,
  };

  let inserted = false;
  if (account.insertBeforeAccountId) {
    const idx = project.accounts.findIndex(
      (a) => a.id === account.insertBeforeAccountId,
    );
    if (idx !== -1) {
      project.accounts.splice(idx, 0, newAccount);
      inserted = true;
    }
  } else if (account.insertAfterAccountId) {
    const idx = project.accounts.findIndex(
      (a) => a.id === account.insertAfterAccountId,
    );
    if (idx !== -1) {
      project.accounts.splice(idx + 1, 0, newAccount);
      inserted = true;
    }
  } else if (account.insertToEnd) {
    project.accounts.push(newAccount);
    inserted = true;
  }

  if (!inserted) {
    const targetType = newAccount.type;
    const targetNum = String(newAccount.accountNumber || "").trim();

    if (/^\d+$/.test(targetNum)) {
      const targetVal = parseInt(targetNum, 10);
      const idx = project.accounts.findIndex((a) => {
        if (a.type !== targetType) return false;
        const aNum = String(a.accountNumber || "").trim();
        if (/^\d+$/.test(aNum)) {
          const aVal = parseInt(aNum, 10);
          return aVal >= targetVal;
        }
        return false;
      });

      if (idx !== -1) {
        project.accounts.splice(idx, 0, newAccount);
        inserted = true;
      }
    }

    if (!inserted) {
      let lastSameTypeIdx = -1;
      project.accounts.forEach((a, idx) => {
        if (a.type === targetType) lastSameTypeIdx = idx;
      });

      if (lastSameTypeIdx !== -1) {
        project.accounts.splice(lastSameTypeIdx + 1, 0, newAccount);
        inserted = true;
      }
    }

    if (!inserted) {
      project.accounts.push(newAccount);
    }
  }

  await updateDoc(
    docRef,
    cleanFirestoreData({
      projects,
      updatedAt: new Date().toISOString(),
    }),
  );

  await recordBudgetHistory(
    year,
    historyMeta || {
      actionType: isTitle ? "TITLE_ADD" : "ACCOUNT_ADD",
      summary: `${isTitle ? "Otsikkorivi" : "Tili"} lisätty: ${
        newAccount.accountNumber || ""
      } ${newAccount.accountName || ""} (${project.name})`,
    },
  );

  return newAccount;
}

/**
 * Updates an existing account's details (e.g. name, category, arvio)
 */
export async function updateAccountInProject(
  year,
  projectId,
  accountId,
  updates,
  historyMeta = null,
) {
  const docRef = doc(db, BUDGETS_COLLECTION, String(year));
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;

  const budget = snap.data();
  const projects = budget.projects || [];
  const project = projects.find(
    (p) =>
      String(p.id) === String(projectId) ||
      String(p.code) === String(projectId),
  );
  if (!project || !project.accounts) return;

  const idx = project.accounts.findIndex((a) => a.id === accountId);
  if (idx !== -1) {
    project.accounts[idx] = {
      ...project.accounts[idx],
      ...updates,
      arvio:
        updates.arvio !== undefined
          ? parseFloat(updates.arvio) || 0
          : project.accounts[idx].arvio,
    };

    // Reorder account within project if placementMode requested
    if (updates.placementMode && updates.placementMode !== "keep") {
      const current = project.accounts.splice(idx, 1)[0];
      if (updates.placementMode === "before" && updates.insertBeforeAccountId) {
        const targetIdx = project.accounts.findIndex(
          (a) => a.id === updates.insertBeforeAccountId,
        );
        project.accounts.splice(targetIdx !== -1 ? targetIdx : 0, 0, current);
      } else if (
        updates.placementMode === "after" &&
        updates.insertAfterAccountId
      ) {
        const targetIdx = project.accounts.findIndex(
          (a) => a.id === updates.insertAfterAccountId,
        );
        project.accounts.splice(
          targetIdx !== -1 ? targetIdx + 1 : project.accounts.length,
          0,
          current,
        );
      } else if (updates.placementMode === "start") {
        project.accounts.unshift(current);
      } else if (updates.placementMode === "end") {
        project.accounts.push(current);
      } else {
        project.accounts.splice(idx, 0, current);
      }
    }

    await updateDoc(
      docRef,
      cleanFirestoreData({
        projects,
        updatedAt: new Date().toISOString(),
      }),
    );

    const isTitle = Boolean(updates.isTitle || updates.isHeader);

    await recordBudgetHistory(
      year,
      historyMeta || {
        actionType: isTitle ? "TITLE_UPDATE" : "ACCOUNT_UPDATE",
        summary: `${isTitle ? "Otsikkoriviä" : "Tiliä"} muokattu: ${
          updates.accountNumber || accountId
        } ${updates.accountName || ""} (${project.name})`,
      },
    );
  }
}

function getAccountSection(acc) {
  if (!acc) return "other";
  const num = String(acc.accountNumber || "").trim();
  const cat = String(acc.category || "").toLowerCase();
  if (
    acc.type === "investment" ||
    cat.includes("investoin") ||
    num.startsWith("X")
  ) {
    return "investment";
  }
  if (
    acc.type === "balance" ||
    acc.type === "equity" ||
    cat.includes("tase") ||
    cat.includes("pääoma") ||
    cat.includes("voitto (tappio)") ||
    ((num.startsWith("1") || num.startsWith("2")) &&
      !num.startsWith("3") &&
      !num.startsWith("4"))
  ) {
    return "balance";
  }
  if (
    acc.type === "income" ||
    cat.includes("tuoto") ||
    num.startsWith("3") ||
    (num.startsWith("9") &&
      !num.startsWith("905") &&
      !num.startsWith("906") &&
      !num.startsWith("908"))
  ) {
    return "income";
  }
  return "expense";
}

function isInvestmentHeader(acc) {
  if (!acc) return false;
  const num = String(acc.accountNumber || "").trim();
  return (
    /^XX\d+/i.test(num) ||
    (/^XX[0-9A-Za-z]/i.test(num) && !num.startsWith("XXX"))
  );
}

/**
 * Moves an account one step up or down within its section / group
 */
export async function moveAccountInProject(
  year,
  projectId,
  accountId,
  direction, // "up" | "down"
) {
  const docRef = doc(db, BUDGETS_COLLECTION, String(year));
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;

  const budget = snap.data();
  const projects = budget.projects || [];
  const project = projects.find(
    (p) =>
      String(p.id) === String(projectId) ||
      String(p.code) === String(projectId),
  );
  if (!project || !project.accounts) return;

  const idx = project.accounts.findIndex((a) => a.id === accountId);
  if (idx === -1) return;

  const currentAcc = project.accounts[idx];
  const section = getAccountSection(currentAcc);

  if (section === "investment" && isInvestmentHeader(currentAcc)) {
    // Current item is an investment group header (e.g. XX1, XX2)
    let groupEnd = idx + 1;
    while (
      groupEnd < project.accounts.length &&
      getAccountSection(project.accounts[groupEnd]) === "investment" &&
      !isInvestmentHeader(project.accounts[groupEnd])
    ) {
      groupEnd++;
    }

    if (direction === "up") {
      let prevHeaderIdx = -1;
      for (let i = idx - 1; i >= 0; i--) {
        const a = project.accounts[i];
        if (getAccountSection(a) === "investment" && isInvestmentHeader(a)) {
          prevHeaderIdx = i;
          break;
        }
      }
      if (prevHeaderIdx !== -1) {
        const groupItems = project.accounts.splice(idx, groupEnd - idx);
        project.accounts.splice(prevHeaderIdx, 0, ...groupItems);
      }
    } else if (direction === "down") {
      let nextHeaderIdx = -1;
      for (let i = groupEnd; i < project.accounts.length; i++) {
        const a = project.accounts[i];
        if (getAccountSection(a) === "investment" && isInvestmentHeader(a)) {
          nextHeaderIdx = i;
          break;
        }
      }
      if (nextHeaderIdx !== -1) {
        let nextGroupEnd = nextHeaderIdx + 1;
        while (
          nextGroupEnd < project.accounts.length &&
          getAccountSection(project.accounts[nextGroupEnd]) === "investment" &&
          !isInvestmentHeader(project.accounts[nextGroupEnd])
        ) {
          nextGroupEnd++;
        }
        const groupItems = project.accounts.splice(idx, groupEnd - idx);
        const insertIdx = nextGroupEnd - groupItems.length;
        project.accounts.splice(insertIdx, 0, ...groupItems);
      }
    }
  } else if (section === "investment") {
    // Current item is an account inside an investment group.
    let minIdx = 0;
    for (let i = idx - 1; i >= 0; i--) {
      const a = project.accounts[i];
      if (isInvestmentHeader(a) || getAccountSection(a) !== "investment") {
        minIdx = i + 1;
        break;
      }
    }

    let maxIdx = project.accounts.length - 1;
    for (let i = idx + 1; i < project.accounts.length; i++) {
      const a = project.accounts[i];
      if (isInvestmentHeader(a) || getAccountSection(a) !== "investment") {
        maxIdx = i - 1;
        break;
      }
    }

    if (direction === "up" && idx > minIdx) {
      const temp = project.accounts[idx];
      project.accounts[idx] = project.accounts[idx - 1];
      project.accounts[idx - 1] = temp;
    } else if (direction === "down" && idx < maxIdx) {
      const temp = project.accounts[idx];
      project.accounts[idx] = project.accounts[idx + 1];
      project.accounts[idx + 1] = temp;
    }
  } else {
    // Standard section: balance, income, expense
    if (direction === "up") {
      let prevIdx = -1;
      for (let i = idx - 1; i >= 0; i--) {
        const a = project.accounts[i];
        if (getAccountSection(a) === section) {
          prevIdx = i;
          break;
        }
      }
      if (prevIdx !== -1) {
        project.accounts.splice(idx, 1);
        project.accounts.splice(prevIdx, 0, currentAcc);
      }
    } else if (direction === "down") {
      let nextIdx = -1;
      for (let i = idx + 1; i < project.accounts.length; i++) {
        const a = project.accounts[i];
        if (getAccountSection(a) === section) {
          nextIdx = i;
          break;
        }
      }
      if (nextIdx !== -1) {
        project.accounts.splice(idx, 1);
        project.accounts.splice(nextIdx, 0, currentAcc);
      }
    }
  }

  await updateDoc(
    docRef,
    cleanFirestoreData({
      projects,
      updatedAt: new Date().toISOString(),
    }),
  );

  await recordBudgetHistory(year, {
    actionType: "ACCOUNT_REORDER",
    summary: `Rivin järjestystä muutettu (${
      direction === "up" ? "ylemmäs" : "alemmas"
    }): ${currentAcc.accountNumber || currentAcc.id} ${
      currentAcc.accountName || ""
    } (${project.name})`,
  });
}

/**
 * Deletes an account from a project
 */
export async function deleteAccountFromProject(
  year,
  projectId,
  accountId,
  historyMeta = null,
) {
  const docRef = doc(db, BUDGETS_COLLECTION, String(year));
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;

  const budget = snap.data();
  const projects = budget.projects || [];
  const project = projects.find(
    (p) =>
      String(p.id) === String(projectId) ||
      String(p.code) === String(projectId),
  );
  if (!project || !project.accounts) return;

  const removedAcc = project.accounts.find((a) => a.id === accountId);
  project.accounts = project.accounts.filter((a) => a.id !== accountId);

  await updateDoc(
    docRef,
    cleanFirestoreData({
      projects,
      updatedAt: new Date().toISOString(),
    }),
  );

  const isTitle = removedAcc
    ? Boolean(removedAcc.isTitle || removedAcc.isHeader)
    : false;

  await recordBudgetHistory(
    year,
    historyMeta || {
      actionType: isTitle ? "TITLE_DELETE" : "ACCOUNT_DELETE",
      summary: `${isTitle ? "Otsikkorivi" : "Tili"} poistettu: ${
        removedAcc?.accountNumber || accountId
      } ${removedAcc?.accountName || ""} (${project.name})`,
    },
  );
}

/**
 * Subscribes to all transactions for a given budget year in real-time
 */
export function subscribeTransactions(year, callback) {
  const q = query(
    collection(db, TRANSACTIONS_COLLECTION),
    where("year", "==", parseInt(year, 10)),
  );

  return onSnapshot(
    q,
    (snap) => {
      const items = [];
      snap.forEach((d) => {
        items.push({ id: d.id, ...d.data() });
      });
      callback(items);
    },
    (err) => {
      console.error(`Error in subscribeTransactions(${year}):`, err);
    },
  );
}

/**
 * Saves an array of vouchers/transactions in a batch write
 */
export async function saveTransactionsBatch(transactions, historyMeta = null) {
  if (!transactions || transactions.length === 0) return;

  // Firestore batches support up to 500 writes per batch
  const chunkSize = 400;
  for (let i = 0; i < transactions.length; i += chunkSize) {
    const batch = writeBatch(db);
    const chunk = transactions.slice(i, i + chunkSize);

    chunk.forEach((tx) => {
      const docId = tx.id || `${tx.year}_${tx.tositelaji}_${tx.tositenro}`;
      const docRef = doc(db, TRANSACTIONS_COLLECTION, docId);
      batch.set(
        docRef,
        cleanFirestoreData({
          ...tx,
          id: docId,
          updatedAt: new Date().toISOString(),
        }),
        { merge: true },
      );
    });

    await batch.commit();
  }

  const txYear = transactions[0]?.year;
  if (txYear) {
    await recordBudgetHistory(
      txYear,
      historyMeta || {
        actionType: "CSV_IMPORT",
        summary: `Tuotu ${transactions.length} tositetta kirjanpitoraportista`,
      },
    );
  }
}

/**
 * Updates a transaction (e.g. reassign to a different account, change status, etc.)
 */
export async function updateTransaction(
  transactionId,
  updates,
  historyMeta = null,
) {
  const docRef = doc(db, TRANSACTIONS_COLLECTION, transactionId);
  const snap = await getDoc(docRef);
  const currentData = snap.exists() ? snap.data() : {};

  await updateDoc(docRef, {
    ...updates,
    updatedAt: new Date().toISOString(),
  });

  const year = updates.year || currentData.year;
  if (year) {
    await recordBudgetHistory(
      year,
      historyMeta || {
        actionType: "TRANSACTION_UPDATE",
        summary: `Tosite päivitetty (#${
          currentData.tositenro || transactionId
        })`,
      },
    );
  }
}

/**
 * Deletes a transaction
 */
export async function deleteTransaction(transactionId, historyMeta = null) {
  const docRef = doc(db, TRANSACTIONS_COLLECTION, transactionId);
  const snap = await getDoc(docRef);
  const currentData = snap.exists() ? snap.data() : {};

  await deleteDoc(docRef);

  if (currentData.year) {
    await recordBudgetHistory(
      currentData.year,
      historyMeta || {
        actionType: "TRANSACTION_DELETE",
        summary: `Tosite poistettu (#${
          currentData.tositenro || transactionId
        })`,
      },
    );
  }
}
